import React, { useCallback, useMemo } from 'react';
import { Chip, Typography } from '@mui/material';
import {
  getApiPresenceStatusMeta,
  getConsentStatusMeta,
  getEnrollmentStatusMeta,
  getHasLiveSelectionsMeta,
  getProductSelectionStatusMeta,
  getTerminationStatusMeta,
} from '../../utils/status';
import ResultsTableNotesCell from '../../components/results-table/ResultsTableNotesCell.jsx';
import { resolveSyntheticCancellationStatus } from '../../utils/dataNormalizer';
import {
  isEnrollmentTerminated,
  resolveApiPresenceStatus,
  resolveEnrollmentLifecycleStatus,
} from '../../utils/enrollmentEvidence';

const buildStatusChip = (meta) => {
  const label = meta?.label || 'Unknown';
  const color = meta?.color && meta.color !== 'default' ? meta.color : 'default';
  const variant = color === 'default' ? 'outlined' : 'filled';
  return <Chip size="small" label={label} color={color} variant={variant} />;
};

const multiSelectFilterFn = (row, id, filterValue) => {
  if (!Array.isArray(filterValue) || filterValue.length === 0) return true;
  const value = row.getValue(id);
  if (Array.isArray(value)) {
    return value.map((item) => String(item ?? '')).some((item) => filterValue.includes(item));
  }
  return filterValue.includes(String(value ?? ''));
};

const multiSelectFilter = {
  filterVariant: 'multi-select',
  filterSelectOptions: [],
  filterFn: multiSelectFilterFn,
};

const useResultsTableColumns = ({
  editingNotesRowId,
  editingNotesValue,
  setEditingNotesRowId,
  setEditingNotesValue,
  setRowOverrides,
  onRowUpdated,
}) => {
  const handleCommitNotes = useCallback(
    (row, nextValue) => {
      const rowId = row?.original?.id;
      if (!rowId) return;
      setRowOverrides((prev) => ({
        ...(prev || {}),
        [rowId]: { ...(prev?.[rowId] || {}), uiNotes: nextValue },
      }));
      onRowUpdated?.({ ...row.original, uiNotes: nextValue });
    },
    [onRowUpdated, setRowOverrides]
  );

  const columns = useMemo(
    () => [
      { accessorKey: 'bac', header: 'BAC', size: 110 },
      { accessorKey: 'diAccountIdsCsv', header: '(DI) Account IDs', size: 240 },
      { accessorKey: 'dealerCd', header: 'Dealer Code', size: 120 },
      { accessorKey: 'dealerName', header: 'Dealer Name', size: 220 },
      { accessorKey: 'stateOrProvince', header: 'State', size: 90 },
      { accessorKey: 'brandNamesDisplay', header: 'Brands', size: 200, ...multiSelectFilter },
      { accessorKey: 'programLabel', header: 'Program', size: 200, ...multiSelectFilter },
      { accessorKey: 'programCode', header: 'Program Code', size: 150, ...multiSelectFilter },
      {
        id: 'enrollmentStatusCd',
        accessorFn: resolveEnrollmentLifecycleStatus,
        header: 'Enrollment Status',
        size: 190,
        ...multiSelectFilter,
        Cell: ({ cell }) => buildStatusChip(getEnrollmentStatusMeta(cell.getValue())),
      },
      {
        id: 'apiPresenceStatus',
        accessorFn: (row) => getApiPresenceStatusMeta(resolveApiPresenceStatus(row)).label,
        header: 'API Presence',
        size: 180,
        enableGlobalFilter: true,
        ...multiSelectFilter,
        Cell: ({ cell }) => buildStatusChip(getApiPresenceStatusMeta(cell.getValue())),
      },
      {
        id: 'syntheticCancellationState',
        accessorFn: resolveSyntheticCancellationStatus,
        header: 'Synthetic Status',
        size: 170,
        ...multiSelectFilter,
        Cell: ({ cell }) => {
          const label = cell.getValue();
          if (!label) return '';
          const color = label === 'Cancelled' ? 'error' : 'warning';
          return (
            <Chip
              size="small"
              label={label}
              color={color}
              data-testid={`synthetic-status-${label.toLowerCase()}`}
            />
          );
        },
      },
      {
        accessorKey: 'programSelectionStatusCd',
        header: 'Program Selection Status',
        size: 210,
        ...multiSelectFilter,
        Cell: ({ cell }) => buildStatusChip(getProductSelectionStatusMeta(cell.getValue())),
      },
      {
        accessorKey: 'productSelectionStatusCd',
        header: 'Product Selection Status',
        size: 210,
        ...multiSelectFilter,
        Cell: ({ cell }) => buildStatusChip(getProductSelectionStatusMeta(cell.getValue())),
      },
      {
        accessorKey: 'programConsentStatusCd',
        header: 'Program Consent',
        size: 190,
        Cell: ({ cell }) => buildStatusChip(getConsentStatusMeta(cell.getValue())),
      },
      {
        accessorKey: 'dataConsentStatusCd',
        header: 'Data Consent',
        size: 170,
        ...multiSelectFilter,
        Cell: ({ cell }) => buildStatusChip(getConsentStatusMeta(cell.getValue())),
      },
      {
        accessorKey: 'hasLiveSelections',
        header: 'Has Live Selections',
        size: 170,
        ...multiSelectFilter,
        Cell: ({ cell }) => buildStatusChip(getHasLiveSelectionsMeta(cell.getValue())),
      },
      {
        accessorKey: 'hasLiveAppsheetProducts',
        header: 'AppSheet Live',
        size: 170,
        ...multiSelectFilter,
        Cell: ({ cell }) => buildStatusChip(getHasLiveSelectionsMeta(cell.getValue())),
      },
      {
        id: 'isTerminated',
        accessorFn: isEnrollmentTerminated,
        header: 'Is Terminated',
        size: 150,
        ...multiSelectFilter,
        Cell: ({ cell }) => buildStatusChip(getTerminationStatusMeta(cell.getValue())),
      },
      {
        id: 'enrollmentDateResolved',
        header: 'Enrollment Date',
        size: 170,
        sortingFn: 'datetime',
        accessorFn: (row) => (row.enrollmentDateSort != null ? new Date(row.enrollmentDateSort).toISOString() : ''),
        Cell: ({ row }) => row.original.enrollmentDateResolved || '',
      },
      {
        id: 'terminationDateResolved',
        header: 'Termination Date',
        size: 170,
        sortingFn: 'datetime',
        accessorFn: (row) => (row.terminationDateSort != null ? new Date(row.terminationDateSort).toISOString() : ''),
        Cell: ({ row }) => row.original.terminationDateResolved || '',
      },
      {
        id: 'lastUpdateResolved',
        header: 'Last Update',
        size: 170,
        sortingFn: 'datetime',
        accessorFn: (row) => (row.lastUpdateSort != null ? new Date(row.lastUpdateSort).toISOString() : ''),
        Cell: ({ row }) => row.original.lastUpdateResolved || '',
      },
      {
        accessorKey: 'vendorSummary',
        header: 'Vendors',
        size: 260,
        ...multiSelectFilter,
        Cell: ({ cell }) => (
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {cell.getValue() || ''}
          </Typography>
        ),
      },
      {
        accessorKey: 'matchedTerm',
        header: 'Matched Term',
        size: 160,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ cell }) => (cell.getValue() ? <Chip size="small" variant="outlined" label={cell.getValue()} /> : ''),
      },
      {
        accessorKey: 'uiNotes',
        header: 'Notes',
        size: 240,
        enableSorting: false,
        Cell: ({ row }) => {
          return (
            <ResultsTableNotesCell
              row={row}
              editingNotesRowId={editingNotesRowId}
              editingNotesValue={editingNotesValue}
              setEditingNotesRowId={setEditingNotesRowId}
              setEditingNotesValue={setEditingNotesValue}
              onCommitNotes={handleCommitNotes}
            />
          );
        },
      },
    ],
    [
      editingNotesRowId,
      editingNotesValue,
      handleCommitNotes,
      setEditingNotesRowId,
      setEditingNotesValue,
    ]
  );

  return { columns };
};

export default useResultsTableColumns;
